// src/pages/ForgotPasswordPage.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, Loader2, CheckCircle2, HelpCircle } from 'lucide-react';
import { requestPasswordReset } from '../api';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);

    try {
      await requestPasswordReset(trimmedEmail);
      setSubmitted(true);
    } catch (err) {
      console.error('Password reset request error:', err);
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message && err.message.includes('Network Error')) {
        setError('Network error. Please check your internet connection.');
      } else {
        setSubmitted(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="flex items-center justify-center min-h-screen bg-cover bg-center bg-no-repeat relative p-4"
      style={{ backgroundImage: "url('/images/login-bg.png')" }}
    >
      {/* Semi-transparent dark overlay */}
      <div className="absolute inset-0 bg-gray-900/60" />

      {/* Main Container */}
      <div className="relative w-full max-w-md p-6 sm:p-8 space-y-6 bg-gray-900/10 backdrop-blur-md shadow-2xl rounded-2xl border border-white/20 z-10 mx-auto">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-3 bg-teal-500/20 border border-teal-500/30 rounded-full flex items-center justify-center text-teal-400">
            <Mail className="w-6 h-6" />
          </div>
          <h2 className="text-2xl font-bold text-white">Reset Password</h2>
          <p className="text-gray-300 text-sm mt-1">
            Enter your account email to receive a password reset link.
          </p>
        </div>

        {submitted ? (
          <div className="space-y-5">
            <div className="bg-teal-500/10 border border-teal-500/30 rounded-xl p-4 text-center space-y-2">
              <CheckCircle2 className="w-8 h-8 text-teal-400 mx-auto" />
              <h3 className="font-semibold text-white text-base">Check Your Inbox</h3>
              <p className="text-gray-300 text-xs leading-relaxed">
                If an account with <span className="text-teal-300 font-medium">{email}</span> exists, we have sent instructions to reset your password.
              </p>
            </div>

            <div className="pt-2">
              <button
                type="button"
                onClick={() => { setSubmitted(false); setEmail(''); setError(''); }}
                className="w-full py-2.5 px-4 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm font-medium transition-colors border border-gray-700"
              >
                Send to a different email
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="reset-email" className="block text-sm font-medium text-gray-100 mb-1">
                Registered Email Address
              </label>
              <div className="relative">
                <input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="player@example.com"
                  className="w-full px-3.5 py-2.5 border border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-white bg-gray-800 text-base"
                  required
                />
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-400 font-medium bg-red-500/10 border border-red-500/20 p-2.5 rounded-lg">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full min-h-[44px] px-4 py-2.5 font-semibold text-gray-900 bg-gradient-to-r from-teal-400 to-teal-500 rounded-lg hover:from-teal-300 hover:to-teal-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Sending Link...</span>
                </>
              ) : (
                <span>Send Reset Link</span>
              )}
            </button>
          </form>
        )}

        {/* Coach Assistance Fallback Notice */}
        <div className="bg-gray-800/80 border border-teal-500/40 rounded-xl p-4 space-y-2 shadow-md">
          <div className="flex items-start gap-2.5">
            <HelpCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" />
            <div className="text-left space-y-1">
              <h4 className="font-semibold text-white text-xs sm:text-sm">
                Registered with a temporary or invalid email?
              </h4>
              <p className="text-gray-300 text-xs leading-relaxed">
                If you do not have access to your email address, please contact your squash coach or club admin. They can reset your password directly from their coach dashboard and provide you with your new login details.
              </p>
            </div>
          </div>
        </div>

        {/* Back to Login Link */}
        <div className="text-center pt-1">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-sm text-teal-400 hover:text-teal-300 font-medium transition-colors p-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Login</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
